import React, { Component } from "react";
import {
  makeKey,
  getStatus,
  subscribe,
  requestRebuild,
} from "../utils/projectionStatusStore";

const ACTIVE_STATES = new Set([
  "queued",
  "processing",
  "retrying",
  "delayed",
  "__loading__",
]);

// Only these states (not __loading__) represent a user-triggered rebuild in progress.
// Transitioning from one of these → "current" earns the checkmark.
const REBUILD_STATES = new Set(["queued", "processing", "retrying", "delayed"]);

const TOOLTIPS = {
  __loading__: "Loading status…",
  queued: "Update queued",
  processing: "Updating…",
  retrying: "Retrying update…",
  delayed: "Update delayed",
  current: null, // set dynamically below
  failed: "Retry update",
  __error__: "Status unavailable",
};

class ProjectionStatusIcon extends Component {
  constructor(props) {
    super(props);
    this.state = { tick: 0, justCompleted: false };
    this._unsubscribe = null;
    this._completedTimer = null;
    this._prevState = null;
    this.handleClick = this.handleClick.bind(this);
  }

  componentDidMount() {
    this._unsubscribe = subscribe(() =>
      this.setState((s) => ({ tick: s.tick + 1 }))
    );
  }

  componentDidUpdate() {
    const { identity } = this.props;
    if (!identity) return;
    const key = makeKey(identity);
    const status = getStatus(key);
    const currentState = status ? status.state : "__loading__";

    // Detect transition from a rebuild state (not initial __loading__) → current
    if (
      REBUILD_STATES.has(this._prevState) &&
      currentState === "current" &&
      !this.state.justCompleted
    ) {
      if (this._completedTimer) clearTimeout(this._completedTimer);
      this.setState({ justCompleted: true });
      this._completedTimer = setTimeout(() => {
        this.setState({ justCompleted: false });
        this._completedTimer = null;
      }, 5000);
    }

    this._prevState = currentState;
  }

  componentWillUnmount() {
    if (this._unsubscribe) this._unsubscribe();
    if (this._completedTimer) clearTimeout(this._completedTimer);
  }

  handleClick(e) {
    e.stopPropagation();
    const { identity } = this.props;
    if (!identity) return;
    const key = makeKey(identity);
    const status = getStatus(key);
    // Block if already active
    if (status && ACTIVE_STATES.has(status.state)) return;
    requestRebuild(identity);
  }

  render() {
    const { identity } = this.props;
    if (!identity) return null;

    const key = makeKey(identity);
    const status = getStatus(key);
    const state = status ? status.state : "__loading__";

    const { justCompleted } = this.state;

    const tooltip =
      justCompleted
        ? "Updated successfully"
        : state === "current"
        ? identity.recordType === "style"
          ? "Refresh style"
          : "Refresh item"
        : TOOLTIPS[state] || "";

    // Just finished — show checkmark for 5 seconds
    if (justCompleted) {
      return (
        <span
          className="projection-status-icon projection-status-icon--done"
          title={tooltip}
          style={{ display: "inline-block", cursor: "default", color: "#27ae60" }}
        >
          <i className="fa fa-check-circle" aria-hidden="true" />
        </span>
      );
    }

    // Spinner states
    if (ACTIVE_STATES.has(state)) {
      return (
        <span
          className="projection-status-icon projection-status-icon--loading"
          title={tooltip}
          style={{ display: "inline-block", cursor: "default", opacity: 0.6 }}
        >
          <i className="fa fa-spinner fa-spin" aria-hidden="true" />
        </span>
      );
    }

    // Failed — show refresh + exclamation
    if (state === "failed") {
      return (
        <span
          className="projection-status-icon projection-status-icon--failed"
          title={tooltip}
          onClick={this.handleClick}
          style={{ display: "inline-block", cursor: "pointer", color: "#c0392b" }}
        >
          <i className="fa fa-refresh" aria-hidden="true" />
          <i
            className="fa fa-exclamation-circle"
            aria-hidden="true"
            style={{
              fontSize: "0.6em",
              verticalAlign: "super",
              marginLeft: "1px",
            }}
          />
        </span>
      );
    }

    // Current — show enabled refresh icon
    if (state === "current") {
      return (
        <span
          className="projection-status-icon projection-status-icon--current"
          title={tooltip}
          onClick={this.handleClick}
          style={{ display: "inline-block", cursor: "pointer", opacity: 0.7 }}
        >
          <i className="fa fa-refresh" aria-hidden="true" />
        </span>
      );
    }

    // Error / unknown — show disabled question mark
    return (
      <span
        className="projection-status-icon projection-status-icon--error"
        title={tooltip}
        style={{ display: "inline-block", cursor: "default", opacity: 0.4 }}
      >
        <i className="fa fa-question-circle" aria-hidden="true" />
      </span>
    );
  }
}

export default ProjectionStatusIcon;
